// TaxInfoDialog.js - COMPLETE WITH VALIDATION

import React, { useCallback, useMemo, memo, useEffect } from 'react';
import {
  Dialog,
  DialogActions,
  DialogContent,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Typography,
  Box,
  Grid,
  Switch,
  FormControlLabel,
  Alert,
  AlertTitle,
  Chip,
  Stack,
  Divider,
  IconButton,
  LinearProgress,
  Card,
  CardContent,
  Avatar,
  Paper,
  Autocomplete,
  useMediaQuery,
} from '@mui/material';
import { createFilterOptions } from '@mui/material/Autocomplete';
import { useTheme, alpha } from '@mui/material/styles';
import {
  Save as SaveIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  AccountBalance as AccountBalanceIcon,
  CheckCircle as CheckCircleIcon,
  Description as DescriptionIcon,
  LocationOn as LocationIcon,
  Assignment as AssignmentIcon,
  Person as PersonIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { COUNTRIES_TAX_CONFIG, SUPPORTED_COUNTRIES } from './constants';
import {
  formatSSN,
  formatEIN,
  formatITIN,
  formatPostalCode,
  validateTaxId,
  isMaskedValue,
  getTaxIdHelperText
} from './../../../../Components/payments/taxWalletInputValid';

// Country dropdown filter - matches on label, ISO code, and currency so
// users can search "Brazil", "BR", or "BRL" and all resolve to Brazil.
const countryFilterOptions = createFilterOptions({
  stringify: (option) => `${option.label} ${option.value} ${option.currency}`,
  trim: true,
});

// ═══════════════════════════════════════════════════════════════════
// MEMOIZED PREVIEW CARD - OUTSIDE MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════
const TaxInfoPreviewCard = memo(({ 
  taxData,
  countryConfig,
  isFormValid,
  completionPercentage
}) => {
  const theme = useTheme();
  
  // Calculate filled fields
  const filledFields = useMemo(() => {
    const fields = [];
    if (taxData.legal_name) fields.push('Legal Name');
    if (taxData.tax_id_number && !isMaskedValue(taxData.tax_id_number)) fields.push('Tax ID');
    if (taxData.address_line1) fields.push('Address');
    if (taxData.city) fields.push('City');
    if (taxData.state && countryConfig.requiresState) fields.push(countryConfig.stateLabel);
    if (taxData.postal_code) fields.push('Postal Code');
    if (taxData.country) fields.push('Country');
    return fields;
  }, [taxData, countryConfig]);

  // Get country info
  const countryInfo = useMemo(() => {
    return SUPPORTED_COUNTRIES.find(c => c.value === taxData.country) || SUPPORTED_COUNTRIES[0];
  }, [taxData.country]);

  return (
    <Card
      elevation={3}
      sx={{
        overflow: 'hidden',
        position: 'sticky',
        top: 16,
      }}
    >
      {/* Header with Icon */}
      <Box
        sx={{
          height: 140,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          position: 'relative',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}
      >
        <Avatar
          sx={{
            width: 80,
            height: 80,
            bgcolor: 'rgba(255,255,255,0.2)',
            backdropFilter: 'blur(10px)',
          }}
        >
          <AccountBalanceIcon sx={{ fontSize: 40, color: 'white' }} />
        </Avatar>
      </Box>

      <CardContent sx={{ pb: 3 }}>
        <Stack spacing={2.5}>
          {/* Title */}
          <Box>
            <Typography variant="h6" fontWeight={700} gutterBottom>
              Tax Information
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Required for payment processing and compliance
            </Typography>
          </Box>

          <Divider />

          {/* Country & Currency */}
          <Box>
            <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
              Tax Jurisdiction
            </Typography>
            <Stack direction="row" spacing={1} alignItems="center">
              <Chip 
                label={countryInfo.label}
                color="primary"
                size="small"
              />
              <Chip 
                label={countryInfo.currency}
                variant="outlined"
                size="small"
              />
            </Stack>
          </Box>

          {/* Legal Name */}
          {taxData.legal_name && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Legal Name
              </Typography>
              <Typography variant="body1" fontWeight={600}>
                {taxData.legal_name}
              </Typography>
              {taxData.business_name && (
                <Typography variant="body2" color="text.secondary">
                  DBA: {taxData.business_name}
                </Typography>
              )}
            </Box>
          )}

          {/* Tax ID */}
          {taxData.tax_id_number && !isMaskedValue(taxData.tax_id_number) && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Tax ID
              </Typography>
              <Stack direction="row" spacing={1} alignItems="center">
                <Typography variant="body1" fontFamily="monospace">
                  {taxData.tax_id_number.replace(/./g, '●').slice(0, -4) + taxData.tax_id_number.slice(-4)}
                </Typography>
                {taxData.tax_id_type && (
                  <Chip 
                    label={taxData.tax_id_type.toUpperCase()}
                    size="small"
                    variant="outlined"
                    color="secondary"
                  />
                )}
              </Stack>
            </Box>
          )}

          {/* Address */}
          {taxData.address_line1 && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Address
              </Typography>
              <Typography variant="body2">
                {taxData.address_line1}
                {taxData.address_line2 && <><br />{taxData.address_line2}</>}
                <br />
                {taxData.city}{taxData.state && `, ${taxData.state}`} {taxData.postal_code}
              </Typography>
            </Box>
          )}

          {/* Tax Forms Status */}
          {(taxData.w9_submitted || taxData.w8_submitted || taxData.t4a_submitted) && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
                Tax Forms Status
              </Typography>
              <Stack spacing={0.5}>
                {taxData.w9_submitted && (
                  <Chip 
                    icon={<CheckCircleIcon />}
                    label="W-9 Submitted"
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                )}
                {taxData.w8_submitted && (
                  <Chip 
                    icon={<CheckCircleIcon />}
                    label="W-8 Submitted"
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                )}
                {taxData.t4a_submitted && (
                  <Chip 
                    icon={<CheckCircleIcon />}
                    label="T4A Submitted"
                    size="small"
                    color="success"
                    variant="outlined"
                  />
                )}
                {taxData.backup_withholding && (
                  <Chip 
                    label="Backup Withholding"
                    size="small"
                    color="warning"
                    variant="outlined"
                  />
                )}
              </Stack>
            </Box>
          )}

          {/* Completion Progress */}
          <Box>
            <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mb: 1 }}>
              <Typography variant="caption" color="text.secondary">
                Form Completion
              </Typography>
              <Typography 
                variant="caption" 
                fontWeight={600} 
                color={isFormValid ? "success.main" : "primary"}
              >
                {completionPercentage}%
              </Typography>
            </Stack>
            <LinearProgress
              variant="determinate"
              value={completionPercentage}
              color={isFormValid ? "success" : "primary"}
              sx={{
                height: 6,
                borderRadius: 3,
                bgcolor: alpha(theme.palette.primary.main, 0.1),
              }}
            />
          </Box>

          {/* Filled Fields Summary */}
          {filledFields.length > 0 && (
            <Box>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                Completed Fields ({filledFields.length})
              </Typography>
              <Stack direction="row" spacing={0.5} flexWrap="wrap" gap={0.5}>
                {filledFields.map((field, idx) => (
                  <Chip
                    key={idx}
                    label={field}
                    size="small"
                    variant="outlined"
                    color="success"
                    icon={<CheckCircleIcon />}
                  />
                ))}
              </Stack>
            </Box>
          )}

          {/* Validation Status */}
          {isFormValid ? (
            <Alert severity="success" icon={<CheckCircleIcon />}>
              <Typography variant="caption">
                ✓ All required fields completed
              </Typography>
            </Alert>
          ) : (
            <Alert severity="info" icon={<InfoIcon />}>
              <Typography variant="caption">
                Complete all required fields to save
              </Typography>
            </Alert>
          )}
        </Stack>
      </CardContent>
    </Card>
  );
}, (prevProps, nextProps) => {
  // Custom comparison - only re-render if these specific props changed
  return (
    JSON.stringify(prevProps.taxData) === JSON.stringify(nextProps.taxData) &&
    JSON.stringify(prevProps.countryConfig) === JSON.stringify(nextProps.countryConfig) &&
    prevProps.isFormValid === nextProps.isFormValid &&
    prevProps.completionPercentage === nextProps.completionPercentage
  );
});

// ═══════════════════════════════════════════════════════════════════
// MEMOIZED FORM CONTENT - OUTSIDE MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════
const TaxFormContent = memo(({
  taxData,
  countryConfig,
  requiredFor,
  handleInputChange,
  handleSelectChange,
  handleSwitchChange,
  taxIdValidation,
  isMobile,
  theme,
  mode
}) => {
  // Memoized sections to prevent unnecessary re-renders
  const taxIdTypeField = useMemo(() => {
    if (!countryConfig.requiresTaxIdType) return null;

    return (
      <Grid item xs={12} sm={6}>
        <FormControl fullWidth margin="normal" required>
          <InputLabel>Tax ID Type</InputLabel>
          <Select
            value={taxData.tax_id_type || ''}
            onChange={handleSelectChange("tax_id_type")}
            label="Tax ID Type"
            sx={{ borderRadius: 2 }}
          >
            {countryConfig.taxIdTypes?.map((type) => (
              <MenuItem key={type.value} value={type.value}>
                {type.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    );
  }, [countryConfig, taxData.tax_id_type, handleSelectChange]);

  const stateField = useMemo(() => {
    // Show the selector whenever state data exists. Countries like US/CA/AU/IN/BR/MX
    // set `requiresState: true`; other countries with state data get an optional selector.
    if (!countryConfig.states?.length) return null;
    const isRequired = !!countryConfig.requiresState;
    const stateLabel = countryConfig.stateLabel || 'State/Province';

    return (
      <Grid item xs={12} sm={4}>
        <FormControl fullWidth margin="normal" required={isRequired}>
          <InputLabel>{isRequired ? stateLabel : `${stateLabel} (optional)`}</InputLabel>
          <Select
            value={taxData.state || ''}
            onChange={handleSelectChange("state")}
            label={isRequired ? stateLabel : `${stateLabel} (optional)`}
            sx={{ borderRadius: 2 }}
          >
            {!isRequired && (
              <MenuItem value="">
                <em>None</em>
              </MenuItem>
            )}
            {countryConfig.states.map((state) => (
              <MenuItem key={state.value} value={state.value}>
                {state.label}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Grid>
    );
  }, [countryConfig, taxData.state, handleSelectChange]);

  const taxFormsSection = useMemo(() => {
    if (!countryConfig.taxForms?.length) return null;

    return (
      <Grid item xs={12}>
        <Paper
          elevation={0}
          sx={{
            p: isMobile ? 2 : 2.5,
            borderRadius: 2,
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: alpha(theme.palette.info.main, 0.05)
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
            <AssignmentIcon color="info" />
            <Typography variant="subtitle1" fontWeight={600}>
              Tax Form Status
            </Typography>
          </Stack>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Track submission status of required tax forms
          </Typography>
          <Stack spacing={1.5}>
            {countryConfig.taxForms.includes('W9') && (
              <FormControlLabel
                control={
                  <Switch
                    checked={taxData.w9_submitted || false}
                    onChange={handleSwitchChange("w9_submitted")}
                    color="success"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      W-9 Form Submitted
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      For U.S. persons
                    </Typography>
                  </Box>
                }
              />
            )}
            {countryConfig.taxForms.includes('W8') && (
              <FormControlLabel
                control={
                  <Switch
                    checked={taxData.w8_submitted || false}
                    onChange={handleSwitchChange("w8_submitted")}
                    color="success"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      W-8 Form Submitted
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      For foreign persons
                    </Typography>
                  </Box>
                }
              />
            )}
            {countryConfig.taxForms.includes('T4A') && (
              <FormControlLabel
                control={
                  <Switch
                    checked={taxData.t4a_submitted || false}
                    onChange={handleSwitchChange("t4a_submitted")}
                    color="success"
                  />
                }
                label={
                  <Box>
                    <Typography variant="body2" fontWeight={600}>
                      T4A Form Submitted
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      For Canadian recipients
                    </Typography>
                  </Box>
                }
              />
            )}
            <Divider sx={{ my: 1 }} />
            <FormControlLabel
              control={
                <Switch
                  checked={taxData.backup_withholding || false}
                  onChange={handleSwitchChange("backup_withholding")}
                  color="warning"
                />
              }
              label={
                <Box>
                  <Typography variant="body2" fontWeight={600}>
                    Subject to Backup Withholding
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    24% withholding on payments
                  </Typography>
                </Box>
              }
            />
          </Stack>
        </Paper>
      </Grid>
    );
  }, [countryConfig, taxData, handleSwitchChange, isMobile, theme]);

  return (
    <>
      {/* Security Notice for Edit Mode */}
      {mode === 'edit' && taxData._edit_mode && taxData._sensitive_fields?.length > 0 && (
        <Alert severity="warning" sx={{ mb: 3 }} icon={<WarningIcon />}>
          <AlertTitle>Security Notice</AlertTitle>
          <Typography variant="body2">
            For security reasons, you must re-enter the following sensitive information:
          </Typography>
          <Box component="ul" sx={{ mt: 1, mb: 0, pl: 2 }}>
            {taxData._sensitive_fields.map((field, idx) => (
              <li key={idx}>
                <Typography variant="body2">
                  {field === 'tax_id_number' ? 'Tax ID Number' : field}
                </Typography>
              </li>
            ))}
          </Box>
        </Alert>
      )}

      {/* Requirement Notice */}
      {requiredFor && (
        <Alert 
          severity="info" 
          sx={{ mb: 3 }} 
          icon={<InfoIcon />}
        >
          <AlertTitle>Tax Information Required</AlertTitle>
          {countryConfig.requirementMessage || 
           `Tax information is required for ${requiredFor.walletType} payments.`}
        </Alert>
      )}

      <Grid container spacing={2}>
        {/* Section 1: Country & Tax ID */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: isMobile ? 2 : 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <LocationIcon color="primary" />
              <Typography variant="subtitle1" fontWeight={600}>
                Tax Jurisdiction
              </Typography>
            </Stack>

            <Grid container spacing={2}>
              {/* Country Selection (searchable) */}
              <Grid item xs={12} sm={6}>
                <Autocomplete
                  fullWidth
                  disableClearable
                  autoHighlight
                  options={SUPPORTED_COUNTRIES}
                  value={
                    SUPPORTED_COUNTRIES.find(
                      (c) => c.value === (taxData.country || 'US')
                    ) ||
                    SUPPORTED_COUNTRIES.find((c) => c.value === 'US') ||
                    SUPPORTED_COUNTRIES[0]
                  }
                  onChange={(_, option) => {
                    if (option?.value) {
                      handleSelectChange('country')({
                        target: { value: option.value },
                      });
                    }
                  }}
                  getOptionLabel={(option) => option?.label || ''}
                  isOptionEqualToValue={(a, b) => a.value === b.value}
                  filterOptions={countryFilterOptions}
                  renderOption={(props, option) => {
                    const { key, ...rest } = props;
                    return (
                      <Box
                        component="li"
                        key={option.value}
                        {...rest}
                        sx={{
                          display: 'flex',
                          alignItems: 'center',
                          gap: 1,
                        }}
                      >
                        <Typography variant="body2" sx={{ flex: 1 }}>
                          {option.label}
                        </Typography>
                        <Chip
                          label={option.currency}
                          size="small"
                          variant="outlined"
                        />
                      </Box>
                    );
                  }}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      label="Country"
                      required
                      margin="normal"
                      placeholder="Search by country, code, or currency"
                      sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    />
                  )}
                />
              </Grid>

              {/* Tax ID Type (country-specific) */}
              {taxIdTypeField}

              {/* Tax ID Number with Validation */}
              {countryConfig.requiresTaxId && (
                <Grid item xs={12} sm={countryConfig.requiresTaxIdType ? 12 : 6}>
                  <TextField
                    fullWidth
                    label={countryConfig.taxIdLabel || "Tax ID Number"}
                    value={taxData.tax_id_number || ''}
                    onChange={handleInputChange("tax_id_number")}
                    required
                    margin="normal"
                    placeholder={countryConfig.taxIdPlaceholder || "Enter tax ID"}
                    error={taxData.tax_id_number && !taxIdValidation.isValid}
                    helperText={taxIdValidation.helperText || `Format: ${countryConfig.taxIdPlaceholder || "Varies by country"}`}
                    sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                    InputProps={{
                      endAdornment: taxData.tax_id_number && taxIdValidation.isValid && !isMaskedValue(taxData.tax_id_number) && (
                        <CheckCircleIcon color="success" sx={{ mr: 1 }} />
                      ),
                    }}
                    inputProps={{
                      maxLength: taxData.tax_id_type === 'ein' ? 10 : 11,
                    }}
                  />
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>

        {/* Section 2: Personal/Business Info */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: isMobile ? 2 : 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <PersonIcon color="primary" />
              <Typography variant="subtitle1" fontWeight={600}>
                Personal Information
              </Typography>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Legal Name"
                  value={taxData.legal_name || ''}
                  onChange={handleInputChange("legal_name")}
                  required
                  margin="normal"
                  placeholder="Your full legal name"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Business Name (Optional)"
                  value={taxData.business_name || ''}
                  onChange={handleInputChange("business_name")}
                  margin="normal"
                  placeholder="DBA or business name"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Section 3: Address Information */}
        <Grid item xs={12}>
          <Paper
            elevation={0}
            sx={{
              p: isMobile ? 2 : 2.5,
              borderRadius: 2,
              border: '1px solid',
              borderColor: 'divider',
            }}
          >
            <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 2 }}>
              <LocationIcon color="primary" />
              <Typography variant="subtitle1" fontWeight={600}>
                Address Information
              </Typography>
            </Stack>

            <Grid container spacing={2}>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 1"
                  value={taxData.address_line1 || ''}
                  onChange={handleInputChange("address_line1")}
                  required
                  margin="normal"
                  placeholder="Street address or P.O. Box"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
              
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address Line 2 (Optional)"
                  value={taxData.address_line2 || ''}
                  onChange={handleInputChange("address_line2")}
                  margin="normal"
                  placeholder="Apartment, suite, unit, building, floor, etc."
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              <Grid item xs={12} sm={4}>
                <TextField
                  fullWidth
                  label="City"
                  value={taxData.city || ''}
                  onChange={handleInputChange("city")}
                  required
                  margin="normal"
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>

              {/* State/Province (if required by country) */}
              {stateField}

              <Grid item xs={12} sm={countryConfig.states?.length ? 4 : 8}>
                <TextField
                  fullWidth
                  label={taxData.country === 'US' ? 'ZIP Code' : 'Postal Code'}
                  value={taxData.postal_code || ''}
                  onChange={handleInputChange("postal_code")}
                  required
                  margin="normal"
                  helperText={
                    taxData.country === 'US' 
                      ? 'Format: 12345 or 12345-6789'
                      : 'Enter postal code'
                  }
                  inputProps={{
                    maxLength: taxData.country === 'US' ? 10 : (taxData.country === 'CA' ? 7 : 10)
                  }}
                  sx={{ '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
                />
              </Grid>
            </Grid>
          </Paper>
        </Grid>

        {/* Section 4: Tax Forms */}
        {taxFormsSection}
      </Grid>

      {/* Country-specific information */}
      {countryConfig.requirementMessage && (
        <Alert severity="info" sx={{ mt: 3 }}>
          <Typography variant="body2">
            {countryConfig.requirementMessage}
          </Typography>
        </Alert>
      )}
    </>
  );
});

// ═══════════════════════════════════════════════════════════════════
// MAIN COMPONENT
// ═══════════════════════════════════════════════════════════════════
const TaxInfoDialog = ({ 
  open, 
  taxData, 
  onClose, 
  onSubmit, 
  onChange,
  requiredFor = null,
  mode = 'create', // 'create' or 'edit'
}) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Get current country configuration
  const countryConfig = useMemo(() => {
    return COUNTRIES_TAX_CONFIG[taxData.country] || COUNTRIES_TAX_CONFIG['US'];
  }, [taxData.country]);

  // Clear masked values when dialog opens in edit mode
  useEffect(() => {
    if (open && taxData._edit_mode) {
      const sensitiveFields = taxData._sensitive_fields || [];
      sensitiveFields.forEach(field => {
        if (taxData[field] && isMaskedValue(taxData[field])) {
          onChange(field, '');
        }
      });
    }
  }, [open, taxData._edit_mode, taxData._sensitive_fields, onChange]);

  // Track validation state for tax ID
  const taxIdValidation = useMemo(() => {
    if (!taxData.tax_id_number) {
      return {
        isValid: false,
        helperText: getTaxIdHelperText(taxData.tax_id_type, false, '', taxData.country)
      };
    }

    if (isMaskedValue(taxData.tax_id_number)) {
      return {
        isValid: false,
        helperText: 'Please re-enter your tax ID for security'
      };
    }

    const isValid = validateTaxId(taxData.tax_id_number, taxData.tax_id_type, taxData.country);
    const helperText = getTaxIdHelperText(taxData.tax_id_type, isValid, taxData.tax_id_number, taxData.country);

    return { isValid, helperText };
  }, [taxData.tax_id_number, taxData.tax_id_type, taxData.country]);

  // Stable event handlers with validation
  const handleInputChange = useCallback((field) => (e) => {
    let value = e.target.value;
    
    // Handle tax ID number with proper formatting
    if (field === 'tax_id_number') {
      const taxIdType = taxData.tax_id_type;
      
      if (taxIdType === 'ssn' || taxIdType === 'itin') {
        value = formatSSN(value);
      } else if (taxIdType === 'ein') {
        value = formatEIN(value);
      } else if (taxIdType) {
        // For foreign/other types: strip formatting, uppercase, and cap at 30.
        // 30 chars comfortably fits long IDs like Indian GSTIN (15), Mexican
        // RFC (13), UAE TRN (15), and SA VAT (15) with headroom.
        value = value.replace(/[^A-Za-z0-9]/g, '').toUpperCase().slice(0, 30);
      }
    }
    
    // Handle postal code formatting
    if (field === 'postal_code') {
      value = formatPostalCode(value, taxData.country || 'US');
    }
    
    // Prevent editing masked values
    if (isMaskedValue(value)) {
      return;
    }
    
    onChange(field, value);
  }, [onChange, taxData.tax_id_type, taxData.country]);

  const handleSelectChange = useCallback((field) => (e) => {
    onChange(field, e.target.value);
  }, [onChange]);

  const handleSwitchChange = useCallback((field) => (e) => {
    onChange(field, e.target.checked);
  }, [onChange]);

  // Check if form is valid for submission with validation
  const isFormValid = useMemo(() => {
    if (!taxData.legal_name?.trim()) return false;
    if (!taxData.address_line1?.trim()) return false;
    if (!taxData.city?.trim()) return false;
    if (!taxData.postal_code?.trim()) return false;
    if (!taxData.country) return false;
    
    if (countryConfig.requiresState && !taxData.state?.trim()) return false;
    
    // Validate tax ID if required
    if (countryConfig.requiresTaxId) {
      if (!taxData.tax_id_number?.trim()) return false;
      if (isMaskedValue(taxData.tax_id_number)) return false;
      if (!validateTaxId(taxData.tax_id_number, taxData.tax_id_type, taxData.country)) return false;
    }
    
    if (countryConfig.requiresTaxIdType && !taxData.tax_id_type) return false;
    
    return true;
  }, [taxData, countryConfig, taxIdValidation]);

  // Calculate completion percentage
  const completionPercentage = useMemo(() => {
    const requiredFields = [
      'legal_name',
      'address_line1',
      'city',
      'postal_code',
      'country'
    ];
    
    if (countryConfig.requiresState) requiredFields.push('state');
    if (countryConfig.requiresTaxId) requiredFields.push('tax_id_number');
    if (countryConfig.requiresTaxIdType) requiredFields.push('tax_id_type');
    
    const filledCount = requiredFields.filter(field => {
      const value = taxData[field];
      if (!value) return false;
      if (typeof value === 'string' && !value.trim()) return false;
      if (field === 'tax_id_number' && isMaskedValue(value)) return false;
      return true;
    }).length;
    
    return Math.round((filledCount / requiredFields.length) * 100);
  }, [taxData, countryConfig]);

  // Memoize form content props
  const formContentProps = useMemo(() => ({
    taxData,
    countryConfig,
    requiredFor,
    handleInputChange,
    handleSelectChange,
    handleSwitchChange,
    taxIdValidation,
    isMobile,
    theme,
    mode
  }), [
    taxData,
    countryConfig,
    requiredFor,
    handleInputChange,
    handleSelectChange,
    handleSwitchChange,
    taxIdValidation,
    isMobile,
    theme,
    mode
  ]);

  if (!open) return null;

  // Mobile View
  if (isMobile) {
    return (
      <Dialog
        open={open}
        onClose={onClose}
        maxWidth="md"
        fullWidth
        fullScreen
        PaperProps={{
          elevation: 0,
          sx: { 
            borderRadius: 0,
            boxShadow: 'none',
            m: 0,
            paddingTop: 'env(safe-area-inset-top, 0px)',
            paddingBottom: 'env(safe-area-inset-bottom, 0px)',
            height: '100vh',
            maxHeight: '100vh',
          }
        }}
      >
        {/* Header */}
        <Box 
          sx={{ 
            background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
            color: 'white',
            position: 'sticky',
            top: 0,
            zIndex: 10,
            boxShadow: theme.shadows[4],
            pt: 'max(16px, env(safe-area-inset-top, 0px))',
          }}
        >
          <Box 
            sx={{ 
              position: 'absolute',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              background: `radial-gradient(circle at 20% 80%, ${alpha(theme.palette.secondary.main, 0.3)} 0%, transparent 70%)`,
              pointerEvents: 'none'
            }}
          />
          
          <Box sx={{ position: 'relative', zIndex: 1, p: 2, pb: 2 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
              <Box sx={{ flex: 1, minWidth: 0 }}>
                <Typography variant="h6" fontWeight={800} sx={{ mb: 0.5 }}>
                  Tax Information
                </Typography>
                <Typography variant="caption" sx={{ opacity: 0.9 }}>
                  Required for payment processing
                </Typography>
              </Box>
              
              <IconButton 
                onClick={onClose}
                size="small"
                sx={{ 
                  color: 'white',
                  bgcolor: 'rgba(255,255,255,0.1)',
                  '&:hover': { bgcolor: 'rgba(255,255,255,0.2)' }
                }}
              >
                <CloseIcon />
              </IconButton>
            </Stack>

            <LinearProgress 
              variant="determinate" 
              value={completionPercentage} 
              sx={{
                height: 4,
                borderRadius: 3,
                bgcolor: 'rgba(255,255,255,0.2)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: 'white',
                  borderRadius: 3,
                }
              }}
            />
          </Box>
        </Box>

        <DialogContent 
          sx={{ 
            p: 2,
            bgcolor: 'background.default',
            overflowY: 'auto',
            flex: 1,
            WebkitOverflowScrolling: 'touch',
            paddingBottom: 'calc(16px + env(safe-area-inset-bottom, 0px))',
          }}
        >
          <TaxFormContent {...formContentProps} />
        </DialogContent>

        <Divider />

        <DialogActions 
          sx={{ 
            p: 2, 
            gap: 1, 
            bgcolor: 'background.paper',
            paddingBottom: 'calc(98px + 16px + env(safe-area-inset-bottom, 0px))',
          }}
        >
          <Button 
            onClick={onClose} 
            color="inherit"
            size="medium"
            sx={{ borderRadius: 2, fontSize: '0.875rem' }}
          >
            Cancel
          </Button>
          <Box sx={{ flex: 1 }} />
          <Button
            onClick={onSubmit}
            variant="contained"
            disabled={!isFormValid}
            startIcon={<SaveIcon />}
            size="medium"
            sx={{ borderRadius: 2, fontSize: '0.875rem' }}
          >
            Save
          </Button>
        </DialogActions>
      </Dialog>
    );
  }

  // Desktop View - Full Screen with Two Columns
  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullScreen
      PaperProps={{
        sx: {
          m: 0,
          maxHeight: '100vh',
          maxWidth: '100vw',
          borderRadius: 0,
        }
      }}
      disablePortal={false}
      keepMounted={false}
      sx={{
        '& .MuiDialog-container': {
          alignItems: 'stretch',
        }
      }}
    >
      {/* Header Bar */}
      <Box
        sx={{
          height: 80,
          borderBottom: '1px solid',
          borderColor: 'divider',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 4,
          bgcolor: 'background.paper',
          backdropFilter: 'blur(20px)',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Box
            sx={{
              width: 48,
              height: 48,
              borderRadius: 2.5,
              background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              color: 'white',
            }}
          >
            <AccountBalanceIcon sx={{ fontSize: 28 }} />
          </Box>
          <Box>
            <Typography variant="h5" sx={{ fontWeight: 700, lineHeight: 1.2 }}>
              Tax Information
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Required for payment processing and tax compliance
            </Typography>
          </Box>
        </Box>

        <Stack direction="row" spacing={2} alignItems="center">
          <LinearProgress
            variant="determinate"
            value={completionPercentage}
            sx={{
              width: 200,
              height: 8,
              borderRadius: 4,
              bgcolor: alpha(theme.palette.primary.main, 0.1),
            }}
          />
          <Typography variant="body2" color="text.secondary" sx={{ minWidth: 40 }}>
            {completionPercentage}%
          </Typography>
          <IconButton 
            onClick={onClose}
            sx={{
              bgcolor: 'action.hover',
              '&:hover': { bgcolor: 'action.selected' },
            }}
          >
            <CloseIcon />
          </IconButton>
        </Stack>
      </Box>

      {/* Main Content - Two Column Layout */}
      <Box sx={{ flex: 1, display: 'flex', overflow: 'hidden' }}>
        {/* Left Column - Preview */}
        <Box
          sx={{
            width: 380,
            borderRight: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            flexDirection: 'column',
            bgcolor: 'background.paper',
            overflowY: 'auto',
            p: 3,
          }}
        >
          <TaxInfoPreviewCard
            taxData={taxData}
            countryConfig={countryConfig}
            isFormValid={isFormValid}
            completionPercentage={completionPercentage}
          />
        </Box>

        {/* Right Column - Form */}
        <Box sx={{ flex: 1, overflowY: 'auto', p: 4 }}>
          <TaxFormContent {...formContentProps} />

          {/* Action Buttons - Sticky at bottom */}
          <Box 
            sx={{ 
              position: 'sticky',
              bottom: 0,
              bgcolor: 'background.default',
              pt: 3,
              pb: 2,
              borderTop: '1px solid',
              borderColor: 'divider',
              mt: 4,
            }}
          >
            <Stack direction="row" spacing={2} justifyContent="flex-end">
              <Button 
                onClick={onClose} 
                color="inherit"
                size="large"
                sx={{ borderRadius: 2, px: 3 }}
              >
                Cancel
              </Button>
              <Button
                onClick={onSubmit}
                variant="contained"
                disabled={!isFormValid}
                startIcon={<SaveIcon />}
                size="large"
                sx={{ borderRadius: 2, px: 4 }}
              >
                Save Tax Information
              </Button>
            </Stack>
          </Box>
        </Box>
      </Box>
    </Dialog>
  );
};

export default React.memo(TaxInfoDialog);